import { apiFetch } from './client'
import type { AutomationEntry, AutomationMode } from '../types'

export const MAX_INSTRUCTIONS = 2000

export interface Automation {
  mode: AutomationMode
  ownerUid: string | null
  fromEmailAddress: string
}

export interface OrgTone {
  id: string
  label: string
  instruction: string
}

export interface OrgSettings {
  orgId: string
  customInstructions: string
  automation: Automation
  /** Shapes this org wrote for itself, shown beside the built-in ones. */
  tones: OrgTone[]
  /** The address Zoho must call, carrying this org's own token. */
  webhookUrl: string
  /** Whether Zoho has any address this org could send from; null if unknown. */
  canSendEmail: boolean | null
  /** Who the Zoho connection belongs to inside the help desk. */
  deskUser: {
    email: string
    name: string
    /** Desk's profile name, which is what decides permissions. */
    profile: string
    /** Desk's role name, which is hierarchy and decides nothing. */
    role: string
    admin: boolean
  }
}

export const getOrgSettings = () => apiFetch<OrgSettings>('getOrgSettings')

/**
 * Send each tone's id back with it. The server keeps an id it recognises, so a
 * tone an agent is already looking at in the panel keeps the id they hold.
 */
export const saveTones = (tones: Array<{ id?: string; label: string; instruction: string }>) =>
  apiFetch<{ tones: OrgTone[] }>('saveTones', { tones })

export const saveOrgInstructions = (customInstructions: string) =>
  apiFetch<{ customInstructions: string }>('saveOrgInstructions', { customInstructions })

export const saveAutomationMode = (mode: AutomationMode) =>
  apiFetch<Automation>('saveAutomationMode', { mode })

/** Separate from the mode: saving an address must not change what automation does. */
export const saveFromAddress = (fromEmailAddress: string) =>
  apiFetch<Automation>('saveFromAddress', { fromEmailAddress })

export const getAutomationActivity = () =>
  apiFetch<{ modes: AutomationMode[]; activity: AutomationEntry[] }>('automationActivity')
